import { html } from '@design.estate/dees-element';
import { demoPage } from '../../../demos/templates.js';
import type { DeesHarnessMessage } from './dees-harness-message.js';

const markdownSample = [
  '## Findings',
  '',
  'The failing test compares against a **stale fixture**. Two options:',
  '',
  '1. Regenerate the fixture from the current schema',
  '2. Pin the schema version in the test setup',
  '',
  '| Option | Effort | Risk |',
  '| --- | --- | --- |',
  '| Regenerate | low | drifts again |',
  '| Pin version | medium | stays honest |',
  '',
  '```typescript',
  'const fixture = await loadFixture({ schemaVersion: 3 });',
  'expect(result).toEqual(fixture);',
  '```',
  '',
  'I recommend pinning — see [the docs](https://example.com/docs).',
].join('\n');

const streamSource = 'Let me look at the build output. The bundler resolved the entry, but the **css tokens** were emitted twice — once from the theme and once from the component. Deduplicating the emission fixes the double-border artifact you saw.';

const startStreamingDemo = (event: MouseEvent) => {
  const container = (event.target as HTMLElement).closest('.messageDemo');
  const target = container?.querySelector<DeesHarnessMessage>('#streamingMessage');
  if (!target) return;
  target.message = {
    id: 'streaming-demo',
    role: 'assistant',
    text: '',
    createdAt: Date.now(),
    streaming: true,
  };
  target.requestUpdate();
  let offset = 0;
  const interval = setInterval(() => {
    const chunk = streamSource.slice(offset, offset + 6);
    offset += 6;
    if (!chunk) {
      clearInterval(interval);
      target.endStream({ inputTokens: 412, outputTokens: 96, totalTokens: 508 });
      return;
    }
    target.appendTextDelta(chunk);
  }, 40);
};

export const demoFunc = () => demoPage('Messages', 'Conversation turns, model details and streaming responses.', html`
  <div class="messageDemo dees-demo-stack">
    <dees-harness-message
      .message=${{
        id: 'u1',
        role: 'user' as const,
        text: 'Why does the badge render with a double border in dark mode?',
        createdAt: Date.now() - 90_000,
        attachments: [
          {
            id: 'a1',
            name: 'screenshot.png',
            mediaType: 'image/png',
            size: 48_213,
            kind: 'image' as const,
          },
          {
            id: 'a2',
            name: 'build.log',
            mediaType: 'text/plain',
            size: 5_120,
            kind: 'text' as const,
            truncated: true,
          },
        ],
      }}
      showTimestamp
    ></dees-harness-message>
    <dees-harness-message
      .message=${{
        id: 'a1',
        role: 'assistant' as const,
        text: markdownSample,
        createdAt: Date.now() - 60_000,
        reasoning: [
          {
            id: 'r1',
            text: 'Compare fixture timestamps against the schema migration dates first.',
            startedAt: Date.now() - 64_000,
            endedAt: Date.now() - 61_500,
          },
        ],
        usage: { inputTokens: 1200, outputTokens: 340, totalTokens: 1540 },
      }}
    ></dees-harness-message>
    <dees-harness-message
      .message=${{
        id: 'e1',
        role: 'assistant' as const,
        text: '',
        createdAt: Date.now() - 30_000,
        error: 'The model endpoint returned 429 — retry in 20s.',
      }}
    ></dees-harness-message>
    <dees-button @clicked=${startStreamingDemo}>Simulate streaming</dees-button>
    <dees-harness-message
      id="streamingMessage"
      .message=${{
        id: 'streaming-demo',
        role: 'assistant' as const,
        text: 'Press “Simulate streaming” above.',
        createdAt: Date.now(),
        markdown: false,
      }}
    ></dees-harness-message>
  </div>
`);
